import { useGetTreinseriesForToday } from "@/entities/treinserie";
import { TreinserieDisplay } from "@/features/infra";
import { ErrorDisplay, FormFieldAutocomplete, FormFieldInput, FormItem, FormLabel } from "@/shared/components";
import { useMemo } from "react";
import { FieldPath, FieldValues, UseFormReturn } from "react-hook-form";
import { getTreinserieOptions } from "./utils";

interface TreinseriesAutocompleteProps<T extends FieldValues> {
    form: UseFormReturn<T>;
    name: FieldPath<T>;
    className?: string;
    readOnly?: boolean;
    placeholder?: string;
    label?: string;
    showSearchIcon?: boolean;
}

export function TreinseriesAutocomplete<T extends FieldValues>({
    form,
    name,
    className,
    readOnly,
    label,
    placeholder,
    showSearchIcon,
}: Readonly<TreinseriesAutocompleteProps<T>>) {
    const { data: treinserieNummers, isPending, error } = useGetTreinseriesForToday();

    const val = form.getValues(name) as string | undefined;
    const options = useMemo(() => {
        const availableTreinseries = getTreinserieOptions(treinserieNummers ?? []);
        const exists = availableTreinseries.find((treinserie) => treinserie.value.toLowerCase() === val?.toLowerCase());
        if (!exists && val !== undefined) {
            return [...availableTreinseries, { label: val, value: val }];
        }
        return availableTreinseries;
    }, [treinserieNummers, val]);
    const currentTreinserie = options.find((treinserie) => treinserie.value.toLowerCase() === val?.toLowerCase());

    if (isPending) {
        return <p>Laden...</p>;
    }

    if (error) {
        return <ErrorDisplay error={error} title="Fout bij het laden van treinseries" />;
    }

    if (!treinserieNummers || treinserieNummers.length === 0) {
        // If there's no treinseries for today (yet), just render a simple FormFieldInput.
        // Same data-testid as the autocomplete branch so tests can target the input
        // regardless of which path renders.
        return <FormFieldInput form={form} name={name} placeholder="Serie" data-testid="treinseries-autocomplete-autocomplete-input" />;
    }

    return readOnly ? (
        <FormItem className={className}>
            <FormLabel>{"Station"}</FormLabel>
            <span className="text-content-placeholder w-full px-3 py-2">{currentTreinserie?.label}</span>
        </FormItem>
    ) : (
        <FormFieldAutocomplete
            data-testid="treinseries-autocomplete"
            allowCustomValue
            form={form}
            label={label}
            name={name}
            placeholder={placeholder ?? "Treinserie"}
            className={className}
            readOnly={readOnly}
            showSearchIcon={showSearchIcon}
            options={options}
            renderOption={(option) => <TreinserieDisplay nummer={option.value} variant="justified" />}
        />
    );
}
